import { BLANK_LABEL, ColumnNameToIndexMap, codeLabel, getReportOptions, updateReportDate } from "./helpers/common";
import { cacheableData, worksheetCache } from "../workbook/cache";
import { track } from "../../clients/mixpanel";
import { splitLineItemsByDimension } from "../utils/lineItems";
import { getEstimateReportData, setPrintAreaToUsedRange, getNamedRangeFromWorksheet, dataExists } from "../workbook";
import { LineItem } from "../../interfaces/LineItem";
import { ValidationError } from "../../errors";
import { getProcessedEstimate } from "../datasources/estimate";
import { transformUseGroups } from "../datasources/useGroups";
import { fetchEstimateExport } from "../../api/estimates";
import { getReportColumnCount } from "./helpers/common";
import { getSOVGroupings } from "./helpers/groupings";
import {
  PreservedContext,
  applyPreservedValuesToRow,
  getPreservedColumnValues,
  getPreservedKeyColumnIndices,
  shouldUsePreservedValues,
} from "./helpers/preservedColumns";
import { createColumnNameToIndexMap } from "./helpers/print";
import { columnNames, rangeNames, rowNames } from "./helpers/rangeNames";
import { RowFormat, printFormattedData } from "./helpers/print";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { FloorGroup } from "../../interfaces/AreaSheet";
import {
  CrossTabSortField,
  aliasCrossTabColumn,
  crossTabColumnCode,
  fillCrossTabBlocks,
  parseCrossTabColumns,
} from "./helpers/crossTab";
import { ensureStylesExist } from "../styles/worksheet";
import { reportLineItemsWithAllocatedMarkups } from "../utils/markups";

export interface SOVReportOptions extends cacheableData {
  groupBy: string;
  useFormulas: boolean;
  freezeGroupLevels: boolean;
  allocateMarkups: boolean;
  splitByUse: boolean;
  /** What fills the `\cross_tab` block; see `parseCrossTabColumns`. */
  crossTabColumns: string;
  /** Prefix cross-tab column headers with their code. */
  showCodesColumns: boolean;
  /** Prefix each row's name with its code. Keeps the pre-split key so existing sheets carry over. */
  showCodes: boolean;
}

interface estimateSOVarrayArgs {
  lineItems: LineItem[];
  columnMap: ColumnNameToIndexMap;
  rowMap: { [key: string]: number };
  useGroups?: ReportUseGroup[];
  floorGroups?: FloorGroup[];
  /** Set when the `\cross_tab` block shows a sort field. */
  crossTabSortField?: CrossTabSortField;
  options: SOVReportOptions;
  preserved?: PreservedContext;
}

export const estimateSOVDefaultOptions = {
  //groupBy: "Bid Package",
  //useFormulas: false,
  freezeGroupLevels: false,
  allocateMarkups: true,
  splitByUse: false,
  crossTabColumns: "",
  showCodesColumns: true,
  showCodes: false,
} as SOVReportOptions;

export const fetchEstimateLineItemsForReport = async (estimateIdParam?: string): Promise<Record<string, any>[]> => {
  let estimateId = estimateIdParam;
  if (!estimateId) {
    const reportData = (await getEstimateReportData()) as any[];
    estimateId = reportData[0]?.estimate_id as string | undefined;
  }
  if (!estimateId) return [];
  const result = await fetchEstimateExport(estimateId);
  if (!result.success) throw result.error;
  if (!result.data) return [];
  return result.data.line_items;
};

export const generateEstimateSOV = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const options = (await getReportOptions(worksheet, estimateSOVDefaultOptions)) as SOVReportOptions;

  const reportData = (await getEstimateReportData()) as any[];
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  const processed = await getProcessedEstimate(estimateId);
  if (!processed) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  await ensureStylesExist();

  const lineItems = processed.baseBidItems.filter((item) => item.type === "line_item");
  const floorGroups = processed.estimateData.floor_groups || [];
  const useGroups = transformUseGroups(processed.estimateData.estimate.stats.useGroups || [], floorGroups);

  useGroups.sort((a, b) => a.order - b.order);
  floorGroups.sort((a, b) => a.label.localeCompare(b.label));

  const totalGsf = useGroups.filter((ug) => !ug.exclude_area).reduce((sum, ug) => sum + (ug.gsf || 0), 0);
  const gsfRange = await getNamedRangeFromWorksheet(worksheet, rangeNames.gsf);
  if (gsfRange) {
    gsfRange.values = [[totalGsf]];
  }

  const markups = processed.baseBidItems.filter(
    (item) => item.type === "markup" || (!options.allocateMarkups && item.type === "atl_markup")
  );

  const crossTabColumns = parseCrossTabColumns(options.crossTabColumns);
  const { crossTabSortField, costDenominatorsRowIndex } = await fillCrossTabBlocks({
    worksheet,
    processed,
    dataRowName: rowNames.estimateSOV,
    lineItems,
    markups,
    useGroups,
    floorGroups,
    crossTabColumns,
    showCodesColumns: options.showCodesColumns,
    isSOV: true,
  });

  const columnMap = await createColumnNameToIndexMap(worksheet);
  aliasCrossTabColumn(columnMap, crossTabColumns);

  const rowMap: { [key: string]: number } = {};
  if (costDenominatorsRowIndex) rowMap[rowNames.costDenominators] = costDenominatorsRowIndex;
  if (!options.groupBy || !options.freezeGroupLevels) {
    options.groupBy = await getSOVGroupings(estimateId);
  }

  if (options.useFormulas && !(await dataExists("Estimate"))) {
    options.useFormulas = false;
  }

  const usePreservedValues = await shouldUsePreservedValues(worksheet, columnMap, estimateId, options.groupBy);
  let preserved: PreservedContext | undefined;
  if (usePreservedValues) {
    const keyColumnIndices = getPreservedKeyColumnIndices(columnMap, options.groupBy);
    const values = await getPreservedColumnValues(worksheet, rowNames.estimateSOV, columnMap, keyColumnIndices);
    preserved = { values, keyColumnIndices };
  }

  const sovDataArray = estimateSOVarray({
    lineItems,
    columnMap,
    rowMap,
    useGroups,
    floorGroups,
    crossTabSortField,
    options,
    preserved,
  });
  const startColumnIndex = columnMap.startColumnIndex;

  await printFormattedData({
    dataArray: sovDataArray,
    worksheet,
    insertAtRangeName: rowNames.estimateSOV,
    startColumnIndex,
    options,
  });

  await worksheetCache.setOptions(worksheet, {
    groupBy: options.groupBy,
    parentId: estimateId,
  });

  if (await getNamedRangeFromWorksheet(worksheet, rowNames.markups)) {
    let markupPreserved: PreservedContext | undefined;
    if (usePreservedValues) {
      const keyColumnIndices = getPreservedKeyColumnIndices(columnMap, "markups");
      const values = await getPreservedColumnValues(worksheet, rowNames.markups, columnMap, keyColumnIndices);
      markupPreserved = { values, keyColumnIndices };
    }
    const { dataArray: markupDataArray } = markupsReportArray(
      markups,
      columnMap,
      rowMap,
      useGroups,
      floorGroups,
      markupPreserved,
      crossTabSortField
    );
    await printFormattedData({
      dataArray: markupDataArray,
      worksheet,
      insertAtRangeName: rowNames.markups,
      startColumnIndex,
    });
  }

  await updateReportDate(worksheet);
  await setPrintAreaToUsedRange(worksheet);
  await worksheet.context.sync();

  track("excel.createReport.estimateSOV", {
    options: options,
    loadTime: (performance.now() - startTime) / 1000,
  });
};

export const splitLineItemsForSOV = (
  items: LineItem[],
  useGroups: ReportUseGroup[],
  floorGroups: FloorGroup[],
  groupBy: string
): LineItem[] => {
  // Split by floor_group
  if (groupBy === "floor_group" && floorGroups.length > 0) {
    return splitLineItemsByDimension(items, floorGroups, "floor_group", "_$$", (group) => group.label);
  }

  // Split by use_group
  if (groupBy === "use_group" && useGroups.length > 0) {
    return splitLineItemsByDimension(items, useGroups, "use_group", "_$", (group) => group.name);
  }

  // For all other groupBy values, return items unchanged
  return items;
};

export const estimateSOVarray = ({
  lineItems,
  columnMap,
  rowMap,
  useGroups = [],
  floorGroups = [],
  crossTabSortField,
  options,
  preserved,
}: estimateSOVarrayArgs): (string | number | null)[][] => {
  let { groupBy, useFormulas, allocateMarkups, splitByUse } = options;
  if (allocateMarkups) {
    lineItems = reportLineItemsWithAllocatedMarkups(lineItems);
  }

  // Split line items if splitByUse is enabled
  let processedLineItems = lineItems;
  if (splitByUse) {
    processedLineItems = splitLineItemsForSOV(lineItems, useGroups, floorGroups, groupBy);
  }

  let sortFieldCode = `${groupBy}_code`;
  let sortFieldDescription = `${groupBy}_desc`;

  if (groupBy === "use_group") {
    sortFieldCode = "use_group";
    sortFieldDescription = "use_group";
    useFormulas = false; //code matching will not work with use_group
  }

  if (groupBy === "floor_group") {
    sortFieldCode = "floor_group";
    sortFieldDescription = "floor_group";
    useFormulas = false; //code matching will not work with use_group
  }

  const isLabelGroupBy = groupBy.startsWith("label_");
  if (isLabelGroupBy) {
    useFormulas = false;
  }

  if (
    !processedLineItems[0]?.hasOwnProperty(sortFieldCode) ||
    !processedLineItems[0]?.hasOwnProperty(sortFieldDescription)
  ) {
    console.warn(`Field ${groupBy} not found in data`);
    // Set useFormulas to false in case the functionality gets enabled in the future
    useFormulas = false;
  }

  const totalColumnIndex = columnMap?.total;
  const codeColumnIndex = columnMap?.code;
  const denominatorRowIndex = rowMap?.[rowNames.costDenominators];
  const firstUseGroupColumnIndex = columnMap?.[columnNames.useGroups];
  const costPerGSFColumnIndex = columnMap?.[columnNames.costPerGSF];

  // The named range requesting useGroups exists
  const useGroupsRequested = firstUseGroupColumnIndex !== undefined;
  const floorGroupsRequested = columnMap?.[columnNames.floorGroups] !== undefined;
  const costPerColumnsRequested = denominatorRowIndex !== undefined;
  const useGroupColumnIndices: { [key: string]: { total: number; gsf?: number; unit?: number } } = {};
  const floorGroupColumnIndices: { [key: string]: { total: number; gsf?: number } } = {};
  const firstCrossTabColumnIndex = columnMap?.[columnNames.crossTab];
  const sortFieldColumnsRequested = crossTabSortField !== undefined && firstCrossTabColumnIndex !== undefined;

  const aggregationMap = new Map<
    string,
    {
      total: number;
      code: string;
      name: string;
      useGroupTotals: { [key: string]: number };
      floorGroupTotals: { [key: string]: number };
      sortFieldTotals: { [code: string]: number };
    }
  >();

  // Aggregate totals per group and use group
  for (const record of processedLineItems) {
    let code = (record[sortFieldCode] as string) || BLANK_LABEL;
    let name = (record[sortFieldDescription] as string) || BLANK_LABEL;
    // For label sort fields, use the combined value as the aggregation key
    // so rows with the same inherited code but different descriptions stay separate
    const aggKey = isLabelGroupBy ? (record[groupBy] as string) || BLANK_LABEL : code;
    const total = Number(record.total) || 0;

    if (!aggregationMap.has(aggKey)) {
      aggregationMap.set(aggKey, {
        total: 0,
        code,
        name,
        useGroupTotals: {},
        floorGroupTotals: {},
        sortFieldTotals: {},
      });
    }

    const entry = aggregationMap.get(aggKey)!;
    entry.total += total;

    // Same `total` as the row, so the sort-field columns always sum back to it
    if (sortFieldColumnsRequested) {
      const columnCode = crossTabColumnCode(record, crossTabSortField.codeKey);
      entry.sortFieldTotals[columnCode] = (entry.sortFieldTotals[columnCode] || 0) + total;
    }

    if (useGroupsRequested && useGroups.length > 0) {
      useGroups.forEach((useGroup) => {
        const useGroupKey = useGroup.id;

        // When split by use and grouped by floor_group, only include the _$ value.
        // if the use group belongs to the current floor group row
        const value =
          splitByUse && groupBy === "floor_group"
            ? useGroup.floor_group === name
              ? Number(record[`${useGroup.name}_$`]) || 0
              : 0
            : Number(record[`${useGroup.name}_$`]) || 0;

        entry.useGroupTotals[useGroupKey] = (Number(entry.useGroupTotals[useGroupKey]) || 0) + value;
      });
    }

    if (floorGroupsRequested && floorGroups.length > 0) {
      floorGroups.forEach((fg) => {
        const floorGroupKey = fg.label;

        const floorGroupValue =
          splitByUse && groupBy === "floor_group"
            ? floorGroupKey === name
              ? Number(record[`${fg.label}_$$`]) || 0
              : 0
            : useGroups
                .filter((ug) => ug.floor_group === fg.label)
                .reduce((sum, useGroup) => sum + (Number(record[`${useGroup.name}_$`]) || 0), 0);

        entry.floorGroupTotals[floorGroupKey] = (Number(entry.floorGroupTotals[floorGroupKey]) || 0) + floorGroupValue;
      });
    }
  }

  // Sort, ensuring "blank" is always last
  const useGroupOrderMap = groupBy === "use_group" ? new Map(useGroups.map((ug) => [ug.name, ug.order])) : undefined;
  const sortedEntries = Array.from(aggregationMap.entries()).sort(([a], [b]) => {
    if (a === BLANK_LABEL) return 1;
    if (b === BLANK_LABEL) return -1;
    if (useGroupOrderMap) {
      const orderA = useGroupOrderMap.get(a);
      const orderB = useGroupOrderMap.get(b);
      if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;
    }
    return a.localeCompare(b);
  });

  if (useGroupsRequested && useGroups.length > 0) {
    let columnIndex = firstUseGroupColumnIndex;
    useGroups.forEach((useGroup) => {
      const useGroupTotalCol = columnIndex++;
      columnMap[useGroup.id] = useGroupTotalCol;

      let gsfCol: number | undefined;
      let unitCol: number | undefined;
      if (useGroup.gsf && useGroup.gsf > 0 && costPerColumnsRequested) {
        gsfCol = columnIndex++;
      }
      if (useGroup.unit_count && useGroup.unit_count > 0 && costPerColumnsRequested) {
        unitCol = columnIndex++;
      }
      useGroupColumnIndices[useGroup.id] = { total: useGroupTotalCol, gsf: gsfCol, unit: unitCol };
    });
  }

  if (floorGroupsRequested && floorGroups.length > 0) {
    let columnIndex = columnMap[columnNames.floorGroups];
    floorGroups.forEach((fg) => {
      const floorGroupTotalCol = columnIndex++;
      columnMap[fg.id] = floorGroupTotalCol;

      let gsfCol: number | undefined;
      if (costPerColumnsRequested) {
        gsfCol = columnIndex++;
      }
      floorGroupColumnIndices[fg.id] = { total: floorGroupTotalCol, gsf: gsfCol };
    });
  }

  const columnCount = Math.max(
    getReportColumnCount(columnMap),
    sortFieldColumnsRequested ? firstCrossTabColumnIndex + crossTabSortField.headers.length : 0
  );
  return sortedEntries.map(([_aggKey, { total, code, name, useGroupTotals, floorGroupTotals, sortFieldTotals }]) => {
    const row: (string | number | null)[] = Array(columnCount).fill(null);

    // Use group and building rows carry their name as the code
    const rowCode = groupBy === "use_group" || groupBy === "floor_group" || code === BLANK_LABEL ? "" : code;

    // Assign basic values
    if (columnMap?.code !== undefined) {
      row[columnMap.code] = groupBy === "use_group" || groupBy === "floor_group" ? "" : code;
    }

    const nameOrDescriptionColumn = columnMap?.name ?? columnMap?.description ?? columnMap?.label;
    if (nameOrDescriptionColumn !== undefined) {
      row[nameOrDescriptionColumn] = codeLabel(rowCode, name, options.showCodes);
    }

    if (columnMap?.total !== undefined) {
      row[columnMap.total] =
        useFormulas && code !== BLANK_LABEL
          ? `=SUMIF(Estimate[${sortFieldCode}],RC[${codeColumnIndex - totalColumnIndex}],Estimate[total])`
          : total;
    }

    if (columnMap[columnNames.costPerGSF] !== undefined && totalColumnIndex !== undefined) {
      row[columnMap[columnNames.costPerGSF]] =
        `=IFERROR(RC[${totalColumnIndex - costPerGSFColumnIndex}]/${rangeNames.gsf},"")`;
    }

    if (preserved) {
      applyPreservedValuesToRow(row, columnMap, preserved);
    }

    if (useGroupsRequested) {
      if (useGroups.length > 0) {
        useGroups.forEach((useGroup) => {
          const { total: totalCol, gsf: gsfCol, unit: unitCol } = useGroupColumnIndices[useGroup.id];
          const useGroupTotal = useGroupTotals[useGroup.id] || 0;

          row[totalCol] =
            useFormulas && code !== BLANK_LABEL
              ? `=SUMIF(Estimate[${sortFieldCode}],RC[${codeColumnIndex - totalCol}],Estimate[${useGroup.name}_$])`
              : useGroupTotal;

          if (gsfCol !== undefined && costPerColumnsRequested) {
            row[gsfCol] = `=IFERROR(RC[-1]/R${denominatorRowIndex + 1}C,0)`;
          }

          if (unitCol !== undefined && costPerColumnsRequested) {
            const c = gsfCol !== undefined ? -2 : -1;
            row[unitCol] = `=IFERROR(RC[${c}]/R${denominatorRowIndex + 1}C,0)`;
          }
        });
      } else {
        // Named range exists but no use groups - write empty string to clear old values
        row[firstUseGroupColumnIndex] = "";
      }
    }

    if (floorGroupsRequested) {
      if (floorGroups.length > 0) {
        floorGroups.forEach((fg) => {
          const { total: totalCol, gsf: gsfCol } = floorGroupColumnIndices[fg.id];

          row[totalCol] = floorGroupTotals[fg.label] || 0;

          if (gsfCol !== undefined && costPerColumnsRequested) {
            row[gsfCol] = `=IFERROR(RC[-1]/R${denominatorRowIndex + 1}C,0)`;
          }
        });
      } else {
        // Named range exists but no floor groups - write empty string to clear old values
        row[columnMap[columnNames.floorGroups]] = "";
      }
    }

    if (sortFieldColumnsRequested) {
      if (crossTabSortField.columns.length > 0) {
        crossTabSortField.columns.forEach((column) => {
          row[firstCrossTabColumnIndex + column.total] = sortFieldTotals[column.code] || 0;
          if (column.costPer && costPerColumnsRequested) {
            row[firstCrossTabColumnIndex + column.costPer.offset] = `=IFERROR(RC[-1]/R${denominatorRowIndex + 1}C,0)`;
          }
        });
      } else {
        row[firstCrossTabColumnIndex] = "";
      }
    }

    return row;
  });
};

export const markupsReportArray = (
  markups: LineItem[], // markups are stored in the Estimate table as line items
  columnMap: ColumnNameToIndexMap,
  rowMap: { [key: string]: number },
  useGroups: ReportUseGroup[],
  floorGroups: FloorGroup[] = [],
  preserved?: PreservedContext,
  crossTabSortField?: CrossTabSortField
) => {
  // Sort markups by order
  if (markups.length === 0) {
    return { dataArray: [], formatList: [] };
  }
  const sortedMarkups = markups
    .slice()
    .sort((a, b) => (a.order ?? "").localeCompare(b.order ?? "", undefined, { numeric: true }));
  const formatList: RowFormat[] = [];

  const totalColumnIndex = columnMap?.total;
  const costPerGSFColumnIndex = columnMap?.[columnNames.costPerGSF];
  const denominatorRowIndex = rowMap?.[rowNames.costDenominators];
  const firstUseGroupColumnIndex = columnMap?.[columnNames.useGroups];

  // Determine if use groups and cost per columns are requested
  const useGroupsRequested = firstUseGroupColumnIndex !== undefined;
  const floorGroupsRequested = columnMap?.[columnNames.floorGroups] !== undefined;
  const costPerColumnsRequested = denominatorRowIndex !== undefined;

  const useGroupColumnIndices: { [key: string]: { total: number; gsf?: number; unit?: number } } = {};
  const floorGroupColumnIndices: { [key: string]: { total: number; gsf?: number } } = {};
  const firstCrossTabColumnIndex = columnMap?.[columnNames.crossTab];
  const sortFieldColumnsRequested = crossTabSortField !== undefined && firstCrossTabColumnIndex !== undefined;

  if (useGroupsRequested && useGroups.length > 0) {
    let columnIndex = firstUseGroupColumnIndex;
    useGroups.forEach((useGroup) => {
      const useGroupTotalCol = columnIndex++;
      columnMap[useGroup.id] = useGroupTotalCol;

      let gsfCol: number | undefined;
      let unitCol: number | undefined;
      if (useGroup.gsf && useGroup.gsf > 0 && costPerColumnsRequested) {
        gsfCol = columnIndex++;
      }
      if (useGroup.unit_count && useGroup.unit_count > 0 && costPerColumnsRequested) {
        unitCol = columnIndex++;
      }
      useGroupColumnIndices[useGroup.id] = { total: useGroupTotalCol, gsf: gsfCol, unit: unitCol };
    });
  }

  if (floorGroupsRequested && floorGroups.length > 0) {
    let columnIndex = columnMap[columnNames.floorGroups];
    floorGroups.forEach((fg) => {
      const floorGroupTotalCol = columnIndex++;
      columnMap[fg.id] = floorGroupTotalCol;

      let gsfCol: number | undefined;
      if (costPerColumnsRequested) {
        gsfCol = columnIndex++;
      }
      floorGroupColumnIndices[fg.id] = { total: floorGroupTotalCol, gsf: gsfCol };
    });
  }

  const columnCount = Math.max(
    getReportColumnCount(columnMap),
    sortFieldColumnsRequested ? firstCrossTabColumnIndex + crossTabSortField.headers.length : 0
  );
  const dataArray = sortedMarkups.map((markup) => {
    const row: (string | number | null)[] = Array(columnCount).fill(null);

    row[columnMap.name] = markup.name;

    if (totalColumnIndex !== undefined) {
      row[columnMap.total] = markup.total;
    }

    if (columnMap[columnNames.markupPercentages] !== undefined) {
      row[columnMap[columnNames.markupPercentages]] = markup.quantity / 100;
    }

    if (columnMap[columnNames.costPerGSF] !== undefined && totalColumnIndex !== undefined) {
      row[columnMap[columnNames.costPerGSF]] = `=IFERROR(RC[${totalColumnIndex - costPerGSFColumnIndex}]/_gsf,"")`;
    }

    if (preserved) {
      applyPreservedValuesToRow(row, columnMap, preserved);
    }

    if (useGroupsRequested) {
      if (useGroups.length > 0) {
        useGroups.forEach((useGroup) => {
          const { total: totalCol, gsf: gsfCol, unit: unitCol } = useGroupColumnIndices[useGroup.id];

          row[totalCol] = markup[`${useGroup.name}_$`] ?? 0;

          if (gsfCol !== undefined && costPerColumnsRequested) {
            row[gsfCol] = `=IFERROR(RC[-1]/R${denominatorRowIndex + 1}C,0)`;
          }

          if (unitCol !== undefined && costPerColumnsRequested) {
            const c = gsfCol !== undefined ? -2 : -1;
            row[unitCol] = `=IFERROR(RC[${c}]/R${denominatorRowIndex + 1}C,0)`;
          }
        });
      } else {
        // Named range exists but no use groups - write empty string to clear old values
        row[firstUseGroupColumnIndex] = "";
      }
    }

    if (floorGroupsRequested) {
      if (floorGroups.length > 0) {
        floorGroups.forEach((fg) => {
          const { total: totalCol, gsf: gsfCol } = floorGroupColumnIndices[fg.id];

          row[totalCol] = markup[`${fg.label}_$$`] ?? 0;

          if (gsfCol !== undefined && costPerColumnsRequested) {
            row[gsfCol] = `=IFERROR(RC[-1]/R${denominatorRowIndex + 1}C,0)`;
          }
        });
      } else {
        // Named range exists but no floor groups - write empty string to clear old values
        row[columnMap[columnNames.floorGroups]] = "";
      }
    }

    if (sortFieldColumnsRequested) {
      if (crossTabSortField.columns.length > 0) {
        const markupColumnCode = crossTabColumnCode(markup, crossTabSortField.codeKey);
        crossTabSortField.columns.forEach((column) => {
          row[firstCrossTabColumnIndex + column.total] =
            column.code === markupColumnCode ? Number(markup.total) || 0 : 0;
          if (column.costPer && costPerColumnsRequested) {
            row[firstCrossTabColumnIndex + column.costPer.offset] = `=IFERROR(RC[-1]/R${denominatorRowIndex + 1}C,0)`;
          }
        });
      } else {
        row[firstCrossTabColumnIndex] = "";
      }
    }

    formatList.push({ style: "markup", group: [], level: 0, indentColumn: columnMap.name });
    return row;
  });

  return { dataArray, formatList };
};
